import { FabricImage, type Canvas, type StaticCanvas } from 'fabric';

interface BackgroundProperties {
  left?: number;
  top?: number;
  scaleX?: number;
  scaleY?: number;
}

const blobToDataURL = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      resolve(reader.result as string);
    });
    reader.addEventListener('error', () => {
      reject(
        new Error(
          `Failed to read background image: ${reader.error?.message ?? 'unknown error'}`,
        ),
      );
    });
    reader.readAsDataURL(blob);
  });

// `setCoords()` is called so Main.tsx can read `backgroundImage.aCoords.tl.y` for goal placement.
const setCanvasBackground = async (
  canvas: Canvas | StaticCanvas,
  source: string,
  properties: BackgroundProperties,
): Promise<void> => {
  const img = new Image();
  const response = await fetch(source);
  // rinus fields are SVG; Dugout's lined pitches are PNG. Either format is inlined as a same-origin
  // data URI (not pointed at the network URL) so the exported canvas stays untainted for `img_data`.
  // Keyed on the extension, not the theme: existing themes only ever pass `.svg`, so their code path
  // — and rendered output — is byte-identical.
  if (source.toLowerCase().endsWith('.svg')) {
    const svgString = await response.text();
    const base64String = window.btoa(svgString);
    img.src = 'data:image/svg+xml;base64,' + base64String;
  } else {
    img.src = await blobToDataURL(await response.blob());
  }
  img.addEventListener('load', () => {
    const fabricImage = new FabricImage(img, {
      crossOrigin: 'anonymous',
      ...properties,
    });
    fabricImage.setCoords();
    canvas.backgroundImage = fabricImage;
    canvas.requestRenderAll();
  });
};

export default setCanvasBackground;
